/**
 * PostgREST client for database access
 * This is the only layer that communicates with the database API
 * 
 * Note: We use @supabase/postgrest-js which is provider-agnostic despite the name.
 * It's the official PostgREST client and works with any PostgREST instance.
 */

import { PostgrestClient } from '@supabase/postgrest-js';

// Import getAccountToken - this is safe because index.ts exports it without importing client.ts
// We'll use a lazy import to avoid circular dependency issues
let getAccountTokenFn: (() => string | null) | null = null;

/**
 * Set the function to get the account token (called from index.ts after initialization)
 */
export function setAccountTokenGetter(fn: () => string | null): void {
    getAccountTokenFn = fn;
}

// Whether the current request prefers the service-role client (set via the
// account context). Injected like the token getter to avoid a circular import.
let getServiceRolePrefFn: (() => boolean) | null = null;

export function setServiceRolePreferenceGetter(fn: () => boolean): void {
    getServiceRolePrefFn = fn;
}

export interface DalConfig {
    url: string;
    apiKey?: string;
    schema?: string;
}

export interface DalConfigWithAdmin extends DalConfig {
    adminApiKey?: string;
}

let clientInstance: PostgrestClient | null = null;
let adminClientInstance: PostgrestClient | null = null;

/**
 * Initialize the DAL client(s)
 * 
 * Creates a normal client using the Publishable key (respects RLS).
 * If adminApiKey is provided, also creates an admin client that bypasses RLS.
 * 
 * @param config - Configuration with url, apiKey (Publishable key), and optional adminApiKey
 * @returns The normal client instance
 */
export function initClient(config: DalConfigWithAdmin): PostgrestClient {
    if (clientInstance) {
        return clientInstance;
    }

    // Normal client (uses Publishable key or service role key if provided)
    // Note: If service role key is used, this bypasses RLS (temporary dev setup)
    // IMPORTANT: The 'littlebooker' schema must be exposed in Supabase PostgREST settings
    // Go to Supabase Dashboard → Settings → API → Exposed schemas
    const dbSchema = config.schema || 'littlebooker';

    // Initialize Supabase context in index.ts for backward compatibility
    // This allows the old DAL functions (listPeople, listHappenings, etc.) to use Supabase instead of in-memory data
    if (config.apiKey) {
        // Convert new config format to old format and set supabaseContext
        const baseUrl = config.url.replace(/\/rest\/v1\/?$/, '');
        const normalizedBase = baseUrl.endsWith('/rest/v1') 
            ? baseUrl 
            : `${baseUrl.replace(/\/$/, '')}/rest/v1`;
        
        // Import and set supabaseContext directly
        import('./index.js').then((dalModule) => {
            const supabaseContextValue = {
                restUrl: normalizedBase,
                headers: {
                    apikey: config.apiKey!,
                    Authorization: `Bearer ${config.apiKey!}`,
                    'Content-Type': 'application/json',
                },
                schema: dbSchema,
                // Thread the service-role key through so the index.ts
                // supabaseRequest path can honour the per-request service-role
                // preference too (§5.9), matching getClient()/getAdminClient().
                adminApiKey: config.adminApiKey,
            };
            // Set supabaseContext so old DAL functions use Supabase
            if ((dalModule as any).setSupabaseContext) {
                (dalModule as any).setSupabaseContext(supabaseContextValue);
                console.log('✅ Supabase context initialized for DAL functions');
            }
        }).catch((err) => {
            console.error('⚠️ Failed to initialize Supabase context for old DAL functions:', err);
        });
    }

    // Create a custom fetch that adds authentication headers
    const createAuthenticatedFetch = (apiKey: string, label: string, allowAccountToken = false) => {
        return async (url: string | URL | Request, init?: RequestInit): Promise<Response> => {
            // Handle different header formats
            const headers = new Headers();

            // Copy existing headers if they exist
            if (init?.headers) {
                if (init.headers instanceof Headers) {
                    init.headers.forEach((value, key) => {
                        headers.set(key, value);
                    });
                } else if (Array.isArray(init.headers)) {
                    init.headers.forEach(([key, value]) => {
                        headers.set(key, value);
                    });
                } else {
                    // Plain object
                    Object.entries(init.headers).forEach(([key, value]) => {
                        headers.set(key, String(value));
                    });
                }
            }

            // Always set auth headers for Supabase/PostgREST
            headers.set('apikey', apiKey);
            // Forward the per-request account JWT (Supabase-valid, minted server
            // side and — in the browser — obtained from login/verify) as the
            // bearer so RLS can scope by its claims. Normal client only; the
            // admin/service-role client must stay service-role. Falls back to the
            // apiKey (anon) when no account token is present, which is the case
            // whenever the Supabase JWT isn't configured yet (RLS still off).
            let bearer = apiKey;
            if (allowAccountToken) {
                const accountToken = getAccountTokenFn?.();
                if (accountToken) bearer = accountToken;
            }
            headers.set('Authorization', `Bearer ${bearer}`);
            headers.set('Accept-Profile', dbSchema);
            headers.set('Content-Profile', dbSchema);

            // Note: Account filtering is done at the application layer (DAL) since RLS is disabled
            // We don't need to pass account tokens to PostgREST - the DAL filters by account_id directly

            // Debug log for server-side (Node.js)
            if (typeof window === 'undefined') {
                const urlStr = typeof url === 'string' ? url : url instanceof URL ? url.toString() : url instanceof Request ? url.url : (url as any).url;
                if (urlStr.includes('/happenings')) {
                    console.log(`🔐 ${label} Request (Server):`, {
                        url: urlStr.substring(0, 100),
                        headers: Object.fromEntries(headers.entries()),
                    });
                }
            }

            // Debug log in development (check if we're in browser)
            if (typeof window !== 'undefined') {
                const urlStr = typeof url === 'string' ? url : url instanceof URL ? url.toString() : url instanceof Request ? url.url : (url as any).url;
                console.log(`🔐 ${label} Request:`, {
                    url: urlStr,
                    hasApikey: headers.has('apikey'),
                    hasAuth: headers.has('Authorization'),
                    hasAccountToken: headers.has('x-account-token'),
                    apiKeyPreview: apiKey ? `${apiKey.substring(0, 10)}...` : 'missing',
                    allHeaders: Object.fromEntries(headers.entries()),
                });
            }

            const response = await fetch(url, {
                ...init,
                headers,
            });

            // Log response status for debugging
            if (typeof window !== 'undefined' && !response.ok) {
                const errorText = await response.clone().text().catch(() => 'Could not read error');
                console.error(`❌ ${label} Request failed:`, {
                    status: response.status,
                    statusText: response.statusText,
                    url: typeof url === 'string' ? url : url instanceof URL ? url.toString() : url.url,
                    error: errorText.substring(0, 200),
                });
            }

            return response;
        };
    };

    // Try using headers in constructor first, fallback to custom fetch
    const clientOptions: any = {
        schema: dbSchema as 'public', // Cast needed for TypeScript, but works with exposed schemas
    };

    // PostgrestClient might accept headers directly
    if (config.apiKey) {
        clientOptions.headers = {
            'apikey': config.apiKey,
            'Authorization': `Bearer ${config.apiKey}`,
            'Accept-Profile': dbSchema,
            'Content-Profile': dbSchema,
        };
        // Also set fetch as fallback
        clientOptions.fetch = createAuthenticatedFetch(config.apiKey, 'Normal', true);

        // Debug log to verify schema headers
        console.log('🔧 DAL Client Config:', {
            url: config.url,
            schema: dbSchema,
            headers: clientOptions.headers,
        });
    }

    clientInstance = new PostgrestClient(config.url, clientOptions) as PostgrestClient;

    // Admin client (uses service role key, bypasses RLS) - optional
    if (config.adminApiKey) {
        const adminOptions: any = {
            schema: dbSchema as 'public',
            headers: {
                'apikey': config.adminApiKey,
                'Authorization': `Bearer ${config.adminApiKey}`,
                'Accept-Profile': dbSchema,
                'Content-Profile': dbSchema,
            },
            fetch: createAuthenticatedFetch(config.adminApiKey, 'Admin'),
        };
        adminClientInstance = new PostgrestClient(config.url, adminOptions) as PostgrestClient;
    }

    return clientInstance!;
}

/**
 * Get the normal client instance (uses Publishable key, respects RLS)
 * Throws if client hasn't been initialized
 */
export function getClient(): PostgrestClient {
    if (!clientInstance) {
        throw new Error('DAL client not initialized. Call initClient() first.');
    }
    // Trusted public endpoints (token-validated, no account JWT) opt into the
    // service-role client to bypass RLS. Falls back to the normal client when
    // the admin client isn't configured (e.g. before the service-role key is
    // provisioned / RLS is still off), which keeps this behaviour-neutral.
    if (getServiceRolePrefFn?.() && adminClientInstance) {
        return adminClientInstance;
    }
    return clientInstance;
}

/**
 * Get the admin client instance (uses service role key, bypasses RLS)
 * Returns null if admin client was not initialized (no adminApiKey provided)
 *
 * ⚠️ Use with caution: This client bypasses Row Level Security policies
 */
export function getAdminClient(): PostgrestClient | null {
    return adminClientInstance;
}

/**
 * Check if admin client is available
 */
export function hasAdminClient(): boolean {
    return adminClientInstance !== null;
}

/**
 * Reset both client instances (useful for testing)
 */
export function resetClient(): void {
    clientInstance = null;
    adminClientInstance = null;
}
